import React, { useEffect, useId, useRef, useState } from 'react';
import { updateStopPayment } from './api';
import { useTranslation } from '../i18n';

export default function PaymentModal({ stop, onClose, onUpdated }) {
  const { t, lang } = useTranslation();
  const titleId = useId();
  const amountDueId = useId();
  const amountPaidId = useId();
  const tipId = useId();
  const notesId = useId();
  const errorId = useId();

  const [form, setForm] = useState({
    amount_charged: stop.amount_charged || '',
    amount_paid: stop.amount_paid || '',
    tip: stop.tip || '',
    payment_notes: stop.payment_notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const firstInputRef = useRef(null);
  const dialogRef = useRef(null);

  // Focus management — move focus into the dialog on mount, restore on unmount.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    if (firstInputRef.current) firstInputRef.current.focus();
    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, []);

  // Escape closes the dialog.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const currencyFormatter = new Intl.NumberFormat(lang === 'es' ? 'es-MX' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const fmt = (n) => currencyFormatter.format(parseFloat(n || 0));

  const charged = parseFloat(form.amount_charged) || 0;
  const paid = parseFloat(form.amount_paid) || 0;
  const tip = parseFloat(form.tip) || 0;
  const balance = charged - paid - tip;

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await updateStopPayment(stop.id, {
        amount_charged: parseFloat(form.amount_charged) || 0,
        amount_paid: parseFloat(form.amount_paid) || 0,
        tip: parseFloat(form.tip) || 0,
        payment_notes: form.payment_notes
      });
      setSaving(false);
      onUpdated(data);
      onClose();
    } catch {
      setError(t('pay.error_generic'));
      setSaving(false);
    }
  };

  const onOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div style={styles.overlay} onClick={onOverlayClick}>
      <div
        ref={dialogRef}
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={error ? errorId : undefined}
      >
        <div style={styles.header}>
          <div>
            <h2 id={titleId} style={styles.title}>
              {t('pay.title', { who: stop.clients?.name || '' })}
            </h2>
            <div style={styles.sub}>{stop.clients?.street}</div>
          </div>
          <button
            type="button"
            style={styles.closeBtn}
            onClick={onClose}
            aria-label={t('a11y.close_dialog')}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        {error && (
          <div id={errorId} role="alert" style={styles.errorBox}>
            {error}
          </div>
        )}

        <div style={styles.field}>
          <label htmlFor={amountDueId} style={styles.label}>{t('pay.amount_due')}</label>
          <input
            ref={firstInputRef}
            id={amountDueId}
            style={styles.input}
            type="number"
            inputMode="decimal"
            placeholder="0.00"
            value={form.amount_charged}
            onChange={e => setForm({ ...form, amount_charged: e.target.value })}
          />
        </div>

        <div style={styles.field}>
          <label htmlFor={amountPaidId} style={styles.label}>{t('pay.amount_paid_today')}</label>
          <input
            id={amountPaidId}
            style={styles.input}
            type="number"
            inputMode="decimal"
            placeholder="0.00"
            value={form.amount_paid}
            onChange={e => setForm({ ...form, amount_paid: e.target.value })}
          />
        </div>

        <div style={styles.field}>
          <label htmlFor={tipId} style={styles.label}>{t('pay.tip')}</label>
          <input
            id={tipId}
            style={styles.input}
            type="number"
            inputMode="decimal"
            placeholder="0.00"
            value={form.tip}
            onChange={e => setForm({ ...form, tip: e.target.value })}
          />
        </div>

        <div style={styles.field}>
          <label htmlFor={notesId} style={styles.label}>{t('pay.notes_optional')}</label>
          <input
            id={notesId}
            style={styles.input}
            type="text"
            placeholder={t('pay.notes_placeholder')}
            value={form.payment_notes}
            onChange={e => setForm({ ...form, payment_notes: e.target.value })}
          />
        </div>

        {charged > 0 && (
          <div style={styles.summary}>
            <div style={styles.summaryRow}>
              <span>{t('pay.amount_due_summary')}</span>
              <span>{fmt(charged)}</span>
            </div>
            <div style={styles.summaryRow}>
              <span>{t('pay.paid_summary')}</span>
              <span style={{ color: '#14653a' }}>{fmt(paid)}</span>
            </div>
            {tip > 0 && (
              <div style={styles.summaryRow}>
                <span>{t('pay.tip_summary')}</span>
                <span style={{ color: '#14653a' }}>{fmt(tip)}</span>
              </div>
            )}
            <div style={{
              ...styles.summaryRow,
              borderTop: '1px solid #eee',
              paddingTop: 8,
              marginTop: 4
            }}>
              <span style={{ fontWeight: 500 }}>{t('pay.balance_due')}</span>
              <span style={{ fontWeight: 500, color: balance > 0 ? '#dc2626' : '#14653a' }}>
                {fmt(Math.max(0, balance))}
              </span>
            </div>
          </div>
        )}

        <div style={styles.actions}>
          <button type="button" style={styles.cancelBtn} onClick={onClose}>
            {t('pay.cancel')}
          </button>
          <button
            type="button"
            style={{ ...styles.saveBtn, ...(saving ? styles.saveBtnDisabled : {}) }}
            onClick={handleSave}
            disabled={saving}
            aria-busy={saving}
          >
            {saving ? t('pay.saving') : t('pay.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  modal: { background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 380, margin: '0 16px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  title: { fontSize: 15, fontWeight: 500, color: '#1a1a1a', margin: 0 },
  sub: { fontSize: 12, color: '#5f6b58', marginTop: 2 },
  closeBtn: { border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: '#5f6b58', padding: 8, minWidth: 32, minHeight: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  errorBox: { background: '#FEF2F2', color: '#dc2626', borderRadius: 8, padding: '8px 10px', fontSize: 13, marginBottom: 12 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 12, fontWeight: 500, color: '#5f6b58', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', color: '#1a1a1a' },
  summary: { background: '#f9f9f9', borderRadius: 8, padding: '10px 12px', marginBottom: 16 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6, color: '#5f6b58' },
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  cancelBtn: { padding: '8px 16px', border: '1px solid #ddd', background: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  saveBtn: { padding: '8px 18px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 13, fontWeight: 500, color: '#fff', cursor: 'pointer' },
  saveBtnDisabled: { opacity: 0.7, cursor: 'wait' }
};
